import React from 'react';
import { X, Instagram, Youtube, Music, ArrowRight, Check } from 'lucide-react';
import { useSocialMediaStore } from '../../../stores/socialMediaStore';
import type { Platform } from '../../../stores/socialMediaStore';
import toast from 'react-hot-toast';

interface SocialMediaConnectModalProps {
  onClose: () => void;
}

export default function SocialMediaConnectModal({ onClose }: SocialMediaConnectModalProps) {
  const { accounts, connectAccount, disconnectAccount } = useSocialMediaStore();

  const handleToggleConnection = (platform: Platform, isConnected: boolean) => {
    if (isConnected) {
      disconnectAccount(platform);
      toast.success(`Conta ${platform} desconectada com sucesso!`);
    } else {
      connectAccount(platform);
      toast.success(`Conta ${platform} conectada com sucesso!`);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl max-w-lg w-full mx-4 p-6 shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Conecte Suas Redes Sociais
            </h2>
            <p className="text-gray-600 mt-1">
              Personalize suas ideias de conteúdo conectando suas redes
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-500 hover:text-gray-700 rounded-lg"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Social Media Accounts */}
        <div className="space-y-4 mb-6">
          {accounts.map((account) => (
            <div
              key={account.platform}
              className="flex items-center justify-between p-4 bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg hover:shadow-md transition-all"
            >
              <div className="flex items-center space-x-3">
                {account.platform === 'instagram' && (
                  <div className="w-10 h-10 rounded-full bg-pink-100 flex items-center justify-center">
                    <Instagram className="h-5 w-5 text-pink-600" />
                  </div>
                )}
                {account.platform === 'youtube' && (
                  <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                    <Youtube className="h-5 w-5 text-red-600" />
                  </div>
                )}
                {account.platform === 'tiktok' && (
                  <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                    <Music className="h-5 w-5 text-black" />
                  </div>
                )}
                <div>
                  <p className="font-medium capitalize">{account.platform}</p>
                  <p className="text-sm text-gray-500">
                    {account.connected 
                      ? `Conectado como ${account.username}`
                      : 'Não conectado'}
                  </p>
                </div>
              </div>
              <button
                onClick={() => handleToggleConnection(account.platform, account.connected)}
                className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center space-x-2 transition-all transform hover:scale-105 ${
                  account.connected
                    ? 'bg-green-50 text-green-600 hover:bg-green-100'
                    : 'bg-indigo-50 text-indigo-600 hover:bg-indigo-100'
                }`}
              >
                {account.connected ? (
                  <>
                    <Check className="h-4 w-4 mr-1" />
                    <span>Conectado</span>
                  </>
                ) : (
                  <>
                    <span>Conectar</span>
                    <ArrowRight className="h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </button>
            </div>
          ))}
        </div>

        {/* Benefits Section */}
        <div className="bg-indigo-50 p-4 rounded-lg mb-6">
          <h3 className="font-medium text-indigo-900 mb-2">Benefícios de conectar suas redes</h3>
          <ul className="space-y-2">
            <li className="flex items-start">
              <div className="w-5 h-5 rounded-full bg-indigo-100 flex items-center justify-center mt-0.5 mr-2 flex-shrink-0">
                <Check className="h-3 w-3 text-indigo-600" />
              </div>
              <span className="text-sm text-indigo-700">Ideias personalizadas para cada plataforma</span>
            </li>
            <li className="flex items-start">
              <div className="w-5 h-5 rounded-full bg-indigo-100 flex items-center justify-center mt-0.5 mr-2 flex-shrink-0">
                <Check className="h-3 w-3 text-indigo-600" />
              </div>
              <span className="text-sm text-indigo-700">Análise de tendências específicas</span>
            </li>
            <li className="flex items-start">
              <div className="w-5 h-5 rounded-full bg-indigo-100 flex items-center justify-center mt-0.5 mr-2 flex-shrink-0">
                <Check className="h-3 w-3 text-indigo-600" />
              </div>
              <span className="text-sm text-indigo-700">Recomendações de conteúdo otimizadas</span>
            </li>
          </ul>
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800"
          >
            Fazer Depois
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors transform hover:scale-105"
          >
            Concluir
          </button>
        </div>
      </div>
    </div>
  );
}